import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  compareItemCode,
  flattenItemTree as flattenItems,
  hasChildItems as isParentItem,
} from "../subcontract-items/itemHierarchy";

const FONT_NAME = "Times New Roman";
const MONEY_FORMAT = "#,##0";
const QUANTITY_FORMAT = "#,##0.####";

const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

// --- LOGIC XỬ LÝ DỮ LIỆU ---
const getValue = (source, key, fallback = "") =>
  source?.[key] ?? source?.[`${key[0].toUpperCase()}${key.slice(1)}`] ?? fallback;

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const safeFileName = (value) =>
  (value || "bien-ban-nghiem-thu")
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 120);

const safeSheetName = (value, fallback = "02A") =>
  (value || fallback).toString().replace(/[\\/?*[\]:]/g, "-").slice(0, 31) || fallback;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

const getDateTime = (profile) => {
  const value = getValue(profile, "acceptanceDate", null);
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const getProfileId = (profile) => getValue(profile, "acceptanceProfileId", null);
const getItemId = (item) => getValue(item, "itemId", null);
const getScopeId = (value) => getValue(value, "subcontractItemScopeId", getValue(value, "scopeId", null));

const isBeforeProfile = (profile, currentProfile) => {
  const profileId = Number(getProfileId(profile) || 0);
  const currentProfileId = Number(getProfileId(currentProfile) || 0);
  const profileTime = getDateTime(profile);
  const currentTime = getDateTime(currentProfile);

  if (currentTime !== null && profileTime !== null && profileTime !== currentTime) {
    return profileTime < currentTime;
  }
  if (currentTime === null && profileTime !== null) return true;
  if (currentTime !== null && profileTime === null) return false;
  return profileId < currentProfileId;
};

const addQuantity = (map, key, value) => {
  if (!key) return;
  map.set(String(key), (map.get(String(key)) || 0) + numericValue(value));
};

const buildScopeMap = (sections = []) => {
  const map = new Map();
  sections.forEach((section) => {
    (section.scopes || section.Scopes || []).forEach((scope) => {
      const scopeId = getScopeId(scope);
      if (!scopeId) return;
      map.set(String(scopeId), {
        ...scope,
        sectionName: getValue(section, "sectionName", getValue(section, "name", "")),
        sectionType: getValue(section, "sectionType", ""),
        sectionDisplayOrder: numericValue(getValue(section, "displayOrder", 0)),
        scopeDisplayOrder: numericValue(getValue(scope, "displayOrder", 0)),
      });
    });
  });
  return map;
};

const getScopeName = (scope, fallback = "Nghiem thu") =>
  getValue(scope, "communeName", "") ||
  getValue(scope, "provinceName", "") ||
  getValue(scope, "scopeName", getValue(scope, "name", fallback));

const buildRows = ({ acceptanceProfile, acceptances, items }) => {
  const itemRows = flattenItems(items);
  const itemById = new Map(itemRows.map((item) => [String(getItemId(item)), item]));
  const currentAcceptedByItem = new Map();
  const currentItemById = new Map();
  const acceptedBeforeByItem = new Map();

  (acceptanceProfile.items || acceptanceProfile.Items || []).forEach((item) => {
    const itemId = getItemId(item);
    addQuantity(currentAcceptedByItem, itemId, getValue(item, "acceptedQuantity", 0));
    if (itemId) currentItemById.set(String(itemId), item);
  });

  (acceptances || [])
    .filter((profile) => String(getProfileId(profile)) !== String(getProfileId(acceptanceProfile)))
    .filter((profile) => isBeforeProfile(profile, acceptanceProfile))
    .forEach((profile) => {
      (profile.items || profile.Items || []).forEach((item) => {
        addQuantity(acceptedBeforeByItem, getItemId(item), getValue(item, "acceptedQuantity", 0));
      });
    });

  const rowItemIds = new Set([
    ...[...currentAcceptedByItem.entries()].filter(([, value]) => value > 0).map(([key]) => key),
    ...[...acceptedBeforeByItem.entries()].filter(([, value]) => value > 0).map(([key]) => key),
  ]);

  return [...rowItemIds]
    .map((itemId) => {
      const sourceItem = itemById.get(String(itemId)) || currentItemById.get(String(itemId)) || {};
      const currentItem = currentItemById.get(String(itemId)) || {};
      return {
        itemId,
        itemCode: getValue(sourceItem, "itemCode", getValue(currentItem, "itemCode", "")),
        itemName: getValue(sourceItem, "itemName", getValue(currentItem, "itemName", "")),
        unit: getValue(sourceItem, "unit", getValue(currentItem, "unit", "")),
        difficultyLevel: getValue(sourceItem, "difficultyLevel", ""),
        subcontractQuantity: numericValue(getValue(sourceItem, "subcontractQuantity", getValue(currentItem, "subcontractQuantity", 0))),
        unitPrice: numericValue(getValue(sourceItem, "unitPrice", getValue(currentItem, "unitPrice", 0))),
        acceptedBefore: acceptedBeforeByItem.get(String(itemId)) || 0,
        acceptedCurrent: currentAcceptedByItem.get(String(itemId)) || 0,
        scopeId: getScopeId(sourceItem) || getScopeId(currentItem),
        isParent: isParentItem(sourceItem),
      };
    })
    .filter((row) => !row.isParent)
    .sort(compareItemCode);
};

// --- HÀM HỖ TRỢ FORMAT BẢNG EXCEL ---

const setCell = (worksheet, address, value, style = {}) => {
  const cell = worksheet.getCell(address);
  if (value !== undefined) cell.value = value;
  cell.font = { name: FONT_NAME, size: 12, ...(style.font || {}) };
  cell.alignment = { vertical: "middle", wrapText: true, ...(style.alignment || {}) };
  if (style.border) cell.border = style.border;
  if (style.fill) cell.fill = style.fill;
  if (style.numFmt) cell.numFmt = style.numFmt;
  return cell;
};

const merge = (worksheet, range) => {
  if (!worksheet.getCell(range.split(":")[0]).isMerged) {
    worksheet.mergeCells(range);
  }
};

const styleRange = (worksheet, startRow, endRow, startCol, endCol, style = {}) => {
  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
    const row = worksheet.getRow(rowIndex);
    for (let colIndex = startCol; colIndex <= endCol; colIndex += 1) {
      const cell = row.getCell(colIndex);
      cell.font = { name: FONT_NAME, size: 12, ...(style.font || {}) };
      cell.alignment = { vertical: "middle", wrapText: true, ...(style.alignment || {}) };
      if (style.border) cell.border = style.border;
      if (style.fill) cell.fill = style.fill;
      if (style.numFmt) cell.numFmt = style.numFmt;
    }
  }
};

const setupColumns = (worksheet) => {
  [7.13, 28.0, 11.25, 10.88, 13.88, 15.0, 13.88, 13.88, 16.0, 16.0, 17.0, 16.0, 18.0, 18.0].forEach(
    (width, index) => {
      worksheet.getColumn(index + 1).width = width;
    }
  );
};

const addHeader = (worksheet, { subcontract, acceptanceProfile, scopeName }) => {
  setupColumns(worksheet);

  merge(worksheet, "M1:N1");
  setCell(worksheet, "M1", "Mẫu số: 2A", {
    font: { bold: true, size: 12 },
    alignment: { horizontal: "right" },
  });

  merge(worksheet, "A2:D2");
  setCell(worksheet, "A2", "CÔNG TY TNHH MTV TĐBĐ", {
    font: { size: 15 },
    alignment: { horizontal: "center" },
  });

  merge(worksheet, "E2:N2");
  setCell(worksheet, "E2", "CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM", {
    font: { bold: true, size: 16 },
    alignment: { horizontal: "center" },
  });

  merge(worksheet, "A3:D3");
  setCell(worksheet, "A3", "XN ……………", {
    font: { size: 14 },
    alignment: { horizontal: "center" },
  });

  merge(worksheet, "E3:N3");
  setCell(worksheet, "E3", "Độc lập - Tự do - Hạnh Phúc", {
    font: { bold: true, size: 15 },
    alignment: { horizontal: "center" },
  });

  merge(worksheet, "A4:D4");
  setCell(worksheet, "A4", "Số: …….", {
    font: { size: 13 },
    alignment: { horizontal: "center" },
  });

  merge(worksheet, "A5:N5");
  setCell(
    worksheet,
    "A5",
    `BIÊN BẢN NGHIỆM THU KHỐI LƯỢNG, CHẤT LƯỢNG VÀ GIÁ TRỊ THANH TOÁN ${formatDate(getValue(acceptanceProfile, "acceptanceDate", ""))}`,
    {
      font: { bold: true, size: 16 },
      alignment: { horizontal: "center" },
    }
  );

  setCell(worksheet, "B6", "Bộ phận sản xuất:", { font: { bold: true, size: 13 } });
  merge(worksheet, "C6:N6");
  setCell(worksheet, "C6", getValue(subcontract, "executionGroupName", "………………"), {
    font: { size: 13 },
    alignment: { horizontal: "left" },
  });

  merge(worksheet, "B7:N7");
  setCell(worksheet, "B7", `Hạng mục công việc: ${scopeName || "………."}`, {
    font: { size: 13 },
    alignment: { horizontal: "left" },
  });

  merge(worksheet, "B8:N8");
  setCell(worksheet, "B8", `Công trình (Công đoạn sản phẩm): ${getValue(subcontract, "subcontractName", "………………")}`, {
    font: { size: 13 },
    alignment: { horizontal: "left" },
  });

  merge(worksheet, "B9:N9");
  setCell(worksheet, "B9", "Căn cứ Phiếu giao khoán công việc;", {
    font: { size: 13 },
    alignment: { horizontal: "left" },
  });

  merge(worksheet, "B10:N10");
  setCell(worksheet, "B10", "Căn cứ khối lượng, chất lượng sản phẩm hoàn thành.", {
    font: { size: 13 },
    alignment: { horizontal: "left" },
  });

  merge(worksheet, "M12:N12");
  setCell(worksheet, "M12", "Đơn vị tính: đồng", {
    font: { italic: true, size: 12 },
    alignment: { horizontal: "right" },
  });

  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].forEach((rowNumber) => {
    worksheet.getRow(rowNumber).height = rowNumber === 5 ? 28 : 22;
  });
};

// --- CHỈNH SỬA TIÊU ĐỀ BẢNG THUỘC TÍNH SỐ 2 ---
const addTableHeader = (worksheet) => {
  const headerRanges = [
    ["A13:A14", "TT"],
    ["B13:B14", "Nội dung công việc"],
    ["C13:C14", "Đơn vị tính"],
    ["D13:D14", "Khó khăn"],
    ["E13:H13", "Khối lượng"],
    ["I13:I14", "Đơn giá sản phẩm"],
    ["J13:N13", "Thành tiền"],
    ["E14:E14", "Theo Phiếu giao khoán"],
    ["F14:F14", "Thanh toán đến cuối kỳ trước"],
    ["G14:G14", "Thanh toán kỳ này"],
    ["H14:H14", "Lũy kế đến hết kỳ này"],
    ["J14:J14", "Theo Phiếu giao khoán"],
    ["K14:K14", "Thanh toán đến cuối kỳ trước"],
    ["L14:L14", "Thanh toán kỳ này"],
    ["M14:M14", "Lũy kế thanh toán đến hết kỳ này"],
    ["N14:N14", "Giá trị còn lại theo phiếu khoán"],
  ];

  headerRanges.forEach(([range, value]) => {
    merge(worksheet, range);
    setCell(worksheet, range.split(":")[0], value, {
      font: { bold: true, size: 12 },
      alignment: { horizontal: "center", vertical: "middle", wrapText: true },
    });
  });

  // Hàng 15: Đánh số cột từ (1) đến (14) độc lập
  const columnNumbers = [
    "(1)", "(2)", "(3)", "(4)", "(5)", "(6)", "(7)",
    "(8=6+7)", "(9)", "(10=5*9)", "(11=6*9)", "(12=7*9)", "(13=8*9)", "(14=10-13)"
  ];

  columnNumbers.forEach((value, index) => {
    setCell(worksheet, worksheet.getRow(15).getCell(index + 1).address, value, {
      font: { bold: true, size: 11 },
      alignment: { horizontal: "center", vertical: "middle" },
    });
  });

  styleRange(worksheet, 13, 15, 1, 14, {
    border: thinBorder,
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center", vertical: "middle", wrapText: true },
  });

  worksheet.getRow(13).height = 28;
  worksheet.getRow(14).height = 32;
  worksheet.getRow(15).height = 20;
};

// --- HÀM TẠO CÁC DÒNG MẶC ĐỊNH I, II, III (SỐ 3) ---
const addSectionRow = (worksheet, rowNumber, code, label) => {
  const row = worksheet.getRow(rowNumber);
  row.getCell(1).value = code;
  row.getCell(2).value = label;
  row.height = 24;
  styleRange(worksheet, rowNumber, rowNumber, 1, 14, {
    border: thinBorder,
    font: { bold: true, size: 12 },
    alignment: { vertical: "middle", wrapText: true },
  });
  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
};

const addSubSectionTextRow = (worksheet, rowNumber, text) => {
  const row = worksheet.getRow(rowNumber);
  row.getCell(2).value = text;
  row.height = 22;
  styleRange(worksheet, rowNumber, rowNumber, 1, 14, {
    border: thinBorder,
    font: { italic: true, size: 11 },
    alignment: { vertical: "middle", wrapText: true },
  });
};

// --- HÀM THÊM DÒNG DỮ LIỆU ĐÃ SỬA LỖI LỆCH CỘT (SỐ 1) ---
const addDataRow = (worksheet, rowNumber, item) => {
  const row = worksheet.getRow(rowNumber);
  row.height = 24;

  // Gán chính xác vị trí từng cột, itemCode ở Cột 1 (A), itemName ở Cột 2 (B)
  row.getCell(1).value = item.itemCode;
  row.getCell(2).value = item.itemName;
  row.getCell(3).value = item.unit;
  row.getCell(4).value = item.difficultyLevel;
  row.getCell(5).value = item.subcontractQuantity;
  row.getCell(6).value = item.acceptedBefore;
  row.getCell(7).value = item.acceptedCurrent;
  row.getCell(8).value = { formula: `F${rowNumber}+G${rowNumber}` };
  row.getCell(9).value = item.unitPrice;
  row.getCell(10).value = { formula: `E${rowNumber}*I${rowNumber}` };
  row.getCell(11).value = { formula: `F${rowNumber}*I${rowNumber}` };
  row.getCell(12).value = { formula: `G${rowNumber}*I${rowNumber}` };
  row.getCell(13).value = { formula: `H${rowNumber}*I${rowNumber}` };
  row.getCell(14).value = { formula: `J${rowNumber}-M${rowNumber}` };

  styleRange(worksheet, rowNumber, rowNumber, 1, 14, {
    border: thinBorder,
    font: { size: 12 },
    alignment: { vertical: "middle", wrapText: true },
  });

  // Căn lề chuẩn
  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(2).alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  row.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(4).alignment = { horizontal: "center", vertical: "middle" };

  [5, 6, 7, 8].forEach((col) => {
    const cell = row.getCell(col);
    cell.alignment = { horizontal: "right", vertical: "middle" };
    cell.numFmt = QUANTITY_FORMAT;
  });

  [9, 10, 11, 12, 13, 14].forEach((col) => {
    const cell = row.getCell(col);
    cell.alignment = { horizontal: "right", vertical: "middle" };
    cell.numFmt = MONEY_FORMAT;
  });
};

const addFooter = (worksheet, totalRow) => {
  let r = totalRow + 2;

  // 1. Đánh giá Chất lượng sản phẩm
  setCell(worksheet, `A${r}`, "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, `B${r}`, "Đánh giá Chất lượng sản phẩm:", { font: { bold: true, size: 12 } });
  r += 1;
  setCell(worksheet, `B${r}`, "Đảm bảo yêu cầu kỹ thuật");
  r += 1;
  setCell(worksheet, `B${r}`, "Đáp ứng đúng thời gian đề ra");
  r += 1;
  setCell(worksheet, `B${r}`, "Sản phẩm giao nộp đúng thời gian yêu cầu đơn vị");
  r += 1;
  setCell(worksheet, `B${r}`, "…………….");
  r += 2;

  // 2. Giá trị tạm ứng trong kỳ
  setCell(worksheet, `A${r}`, "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, `B${r}`, "Giá trị tạm ứng trong kỳ:                 ….. đồng");
  r += 1;
  setCell(worksheet, `B${r}`, "Nhân công:  ….. Đồng");
  r += 1;
  setCell(worksheet, `B${r}`, "Vật tư, VPP:  ….. đồng ");
  r += 1;
  setCell(worksheet, `B${r}`, "Chi phí khác: ….. Đồng");
  r += 2;

  // 3. Giá trị thanh toán kỳ này
  setCell(worksheet, `A${r}`, "-", { alignment: { horizontal: "center" } });
  merge(worksheet, `B${r}:N${r}`);
  setCell(
    worksheet,
    `B${r}`,
    { formula: `"Giá trị thanh toán kỳ này: "&TEXT(L${totalRow},"#,##0")&" đồng"` },
    { font: { bold: true, size: 13 }, alignment: { horizontal: "left" } }
  );
  r += 1;
  setCell(worksheet, `B${r}`, "Nhân công:  ….. Đồng");
  r += 1;
  setCell(worksheet, `B${r}`, "Vật tư, VPP:  ….. Đồng (Theo thực tế)");
  r += 1;
  setCell(worksheet, `B${r}`, "Chi phí khác: ….. Đồng");
  r += 2;

  // 4. Giá trị còn lại được thanh toán
  setCell(worksheet, `A${r}`, "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, `B${r}`, "Giá trị còn lại được thanh toán:                 ….. đồng");
  r += 1;
  setCell(worksheet, `B${r}`, "Bằng chữ: …. Đồng, trong đó:");
  r += 1;
  setCell(worksheet, `B${r}`, "Nhân công:  ….. Đồng");
  r += 1;
  setCell(worksheet, `B${r}`, "Vật tư, VPP:  ….. đồng ");
  r += 1;
  setCell(worksheet, `B${r}`, "Chi phí khác: ….. Đồng");
  r += 2;

  // 5. Sản phẩm giao nộp
  setCell(worksheet, `A${r}`, "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, `B${r}`, "Sản phẩm giao nộp:");
  r += 1;
  setCell(worksheet, `B${r}`, "Hồ sơ sản phẩm phục vụ KTNT theo quy định.");
  r += 1;
  setCell(worksheet, `B${r}`, "…………….");
  r += 2;

  // 6. Chữ ký
  const signRow = r;
  worksheet.getRow(signRow).height = 24;
  worksheet.getRow(signRow + 1).height = 20;

  const signatures = [
    ["A", "C", "NGƯỜI NHẬN KHOÁN"],
    ["D", "G", "PHỤ TRÁCH BỘ PHẬN"],
    ["H", "I", "PHÒNG KHTH"],
    ["J", "L", "PHÒNG TC-KT"],
    ["M", "N", "GIÁM ĐỐC"],
  ];

  signatures.forEach(([start, end, label]) => {
    const rangeTitle = `${start}${signRow}:${end}${signRow}`;
    const rangeSubtitle = `${start}${signRow + 1}:${end}${signRow + 1}`;
    merge(worksheet, rangeTitle);
    merge(worksheet, rangeSubtitle);

    setCell(worksheet, `${start}${signRow}`, label, {
      font: { bold: true, size: 12 },
      alignment: { horizontal: "center", vertical: "middle" },
    });

    setCell(
      worksheet,
      `${start}${signRow + 1}`,
      label === "GIÁM ĐỐC" ? "(Ký tên, đóng dấu)" : "(Ký, ghi rõ họ tên)",
      {
        font: { italic: true, size: 11 },
        alignment: { horizontal: "center", vertical: "middle" },
      }
    );
  });
};

const createScopeSheet = (workbook, { sheetName, rows, subcontract, acceptanceProfile }) => {
  const worksheet = workbook.addWorksheet(safeSheetName(sheetName));

  worksheet.properties.defaultRowHeight = 18;
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.5,
      bottom: 0.5,
      header: 0.2,
      footer: 0.2,
    },
  };

  addHeader(worksheet, { subcontract, acceptanceProfile, scopeName: sheetName });
  addTableHeader(worksheet);

  // Dòng tiêu đề I: Hạng mục công việc (Nhân công)
  addSectionRow(worksheet, 16, "I", "Hạng mục công việc (Nhân công)");

  // Dữ liệu công việc từ buildRows được chèn bên dưới dòng I
  const dataStartRow = 17;
  const exportItems = rows.length > 0 ? rows : [{ itemCode: "…………..", itemName: "" }];
  exportItems.forEach((item, index) => addDataRow(worksheet, dataStartRow + index, item));

  const dataEndRow = dataStartRow + exportItems.length - 1;

  // Dòng tiêu đề II: Vật tư, văn phòng phẩm
  const sec2Row = dataEndRow + 1;
  addSectionRow(worksheet, sec2Row, "II", "Vật tư, văn phòng phẩm");
  addSubSectionTextRow(
    worksheet,
    sec2Row + 1,
    "(Chỉ ghi tổng giá trị thanh toán kỳ này, có bảng thống kê và hóa đơn, chứng từ của tháng kèm theo)"
  );

  // Dòng tiêu đề III: Chi phí khác
  const sec3Row = sec2Row + 2;
  addSectionRow(worksheet, sec3Row, "III", "Chi phí khác");
  addSubSectionTextRow(
    worksheet,
    sec3Row + 1,
    "(Chỉ ghi tổng giá trị thanh toán kỳ này, có bảng thống kê và hóa đơn, chứng từ của tháng kèm theo)"
  );

  // Dòng Tổng cộng
  const totalRow = sec3Row + 2;
  const row = worksheet.getRow(totalRow);
  row.height = 24;

  merge(worksheet, `A${totalRow}:D${totalRow}`);
  setCell(worksheet, `A${totalRow}`, "Tổng cộng", {
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center", vertical: "middle" },
  });

  styleRange(worksheet, totalRow, totalRow, 1, 14, {
    border: thinBorder,
    font: { bold: true, size: 12 },
    alignment: { vertical: "middle" },
  });

  for (let col = 5; col <= 14; col += 1) {
    const letter = worksheet.getColumn(col).letter;
    const cell = worksheet.getCell(totalRow, col);
    cell.value = { formula: `SUM(${letter}${dataStartRow}:${letter}${dataEndRow})` };
    cell.alignment = { horizontal: "right", vertical: "middle" };
    cell.font = { name: FONT_NAME, size: 12, bold: true };
    cell.numFmt = col <= 8 ? QUANTITY_FORMAT : MONEY_FORMAT;
  }

  addFooter(worksheet, totalRow);
};

export const exportAcceptanceReport = async ({
  subcontract = {},
  acceptanceProfile,
  acceptances = [],
  items = [],
  sections = [],
  exportedBy = "",
}) => {
  if (!acceptanceProfile) throw new Error("Không tìm thấy kỳ nghiệm thu cần xuất.");

  const scopeById = buildScopeMap(sections);
  const rows = buildRows({ acceptanceProfile, acceptances, items });
  const groups = new Map();

  rows.forEach((row) => {
    const scope = scopeById.get(String(row.scopeId)) || {};
    const key = row.scopeId ? String(row.scopeId) : "unknown";
    const current = groups.get(key) || {
      scope,
      scopeName: getScopeName(scope, "Nghiem thu"),
      displayOrder: numericValue(scope.sectionDisplayOrder) * 1000 + numericValue(scope.scopeDisplayOrder),
      rows: [],
    };
    current.rows.push(row);
    groups.set(key, current);
  });

  const exportGroups = [...groups.values()]
    .filter((group) => group.rows.length > 0)
    .sort((left, right) => left.displayOrder - right.displayOrder || left.scopeName.localeCompare(right.scopeName, "vi"));

  if (!exportGroups.length) throw new Error("Kỳ nghiệm thu không có hạng mục đủ điều kiện để xuất.");

  const workbook = new ExcelJS.Workbook();
  workbook.creator = exportedBy || "Project Trak";
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.calcProperties.fullCalcOnLoad = true;

  exportGroups.forEach((group) => createScopeSheet(workbook, {
    sheetName: group.scopeName,
    rows: group.rows,
    subcontract,
    acceptanceProfile,
  }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const suffix = safeFileName(getValue(acceptanceProfile, "acceptanceName", "ky-nghiem-thu"));
  saveAs(blob, `${safeFileName(getValue(subcontract, "subcontractNumber", getValue(subcontract, "subcontractName", "giao-khoan")))}_BBNT_2A_${suffix}.xlsx`);
};

export default exportAcceptanceReport;